import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowUp,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Copy,
  FolderPlus,
  Trash2,
  Box,
} from 'lucide-react';
import type { BlockNode, StyleTargetState } from '../types';
import { BLOCK_MANIFESTS } from '../blocks/generated/manifest';

export interface CanvasToolsOverlayProps {
  selectedNode: BlockNode | null;
  parentNode: BlockNode | null;
  activeState?: StyleTargetState;
  showOutlines?: boolean;
  showSpacingGuides?: boolean;
  highlightedBoxPart?: 'margin' | 'padding' | null;
  zoom?: number;
  onSelectParent?: () => void;
  onMoveNode?: (direction: 'up' | 'down') => void;
  onDuplicateNode?: () => void;
  onWrapInContainer?: () => void;
  onDeleteNode?: () => void;
  onResizeNode?: (patch: { width?: string; height?: string; minHeight?: string }) => void;
}

export function CanvasToolsOverlay({
  selectedNode,
  parentNode,
  activeState = 'default',
  showSpacingGuides = false,
  highlightedBoxPart = null,
  zoom = 1,
  onSelectParent,
  onMoveNode,
  onDuplicateNode,
  onWrapInContainer,
  onDeleteNode,
  onResizeNode,
}: CanvasToolsOverlayProps): React.JSX.Element | null {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [resizingHandle, setResizingHandle] = useState<string | null>(null);
  const [liveDimension, setLiveDimension] = useState<{ width: number; height: number } | null>(null);
  const startDragRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);

  // Track position of the selected element inside canvas
  useEffect(() => {
    if (!selectedNode) {
      setRect(null);
      return;
    }

    const updateRect = () => {
      const el = document.querySelector(`[data-node-id="${selectedNode.id}"]`) as HTMLElement;
      if (el) {
        const domRect = el.getBoundingClientRect();
        const canvasContainer = el.closest('.wb-artboard-container');
        if (canvasContainer) {
          const containerRect = canvasContainer.getBoundingClientRect();
          setRect(new DOMRect(
            (domRect.left - containerRect.left) / zoom,
            (domRect.top - containerRect.top) / zoom,
            domRect.width / zoom,
            domRect.height / zoom
          ));
        } else {
          setRect(domRect);
        }
      } else {
        setRect(null);
      }
    };

    updateRect();
    const interval = setInterval(updateRect, 100);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [selectedNode, zoom]);

  // Handle 8-point resizing
  const handlePointerDown = (e: React.PointerEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!rect) return;

    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setResizingHandle(handle);
    startDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: rect.width,
      startH: rect.height,
    };
    setLiveDimension({ width: Math.round(rect.width), height: Math.round(rect.height) });
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!resizingHandle || !startDragRef.current) return;
    e.preventDefault();

    const dx = (e.clientX - startDragRef.current.startX) / zoom;
    const dy = (e.clientY - startDragRef.current.startY) / zoom;

    let newW = startDragRef.current.startW;
    let newH = startDragRef.current.startH;

    if (resizingHandle.includes('r')) newW = Math.max(40, startDragRef.current.startW + dx);
    if (resizingHandle.includes('l')) newW = Math.max(40, startDragRef.current.startW - dx);
    if (resizingHandle.includes('b')) newH = Math.max(30, startDragRef.current.startH + dy);
    if (resizingHandle.includes('t')) newH = Math.max(30, startDragRef.current.startH - dy);

    setLiveDimension({ width: Math.round(newW), height: Math.round(newH) });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!resizingHandle || !liveDimension) return;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    if (onResizeNode) {
      if (resizingHandle === 'cr' || resizingHandle === 'cl') {
        onResizeNode({ width: `${liveDimension.width}px` });
      } else if (resizingHandle === 'bc' || resizingHandle === 'tc') {
        onResizeNode({ minHeight: `${liveDimension.height}px` });
      } else {
        onResizeNode({ width: `${liveDimension.width}px`, minHeight: `${liveDimension.height}px` });
      }
    }

    setResizingHandle(null);
    setLiveDimension(null);
    startDragRef.current = null;
  };

  if (!selectedNode || !rect) return null;

  const manifest = (BLOCK_MANIFESTS as Record<string, any>)[selectedNode.type];
  const label = selectedNode.customName || manifest?.title || selectedNode.type;
  const tagId = selectedNode.htmlId ? `#${selectedNode.htmlId}` : `#${selectedNode.id.slice(0, 6)}`;

  // Parse margins and paddings for box overlays
  const baseStyles = selectedNode.styles?.base || {};
  const mTop = parseInt(baseStyles.marginTop || baseStyles.margin || '0', 10) || 0;
  const mRight = parseInt(baseStyles.marginRight || baseStyles.margin || '0', 10) || 0;
  const mBottom = parseInt(baseStyles.marginBottom || baseStyles.margin || '0', 10) || 0;
  const mLeft = parseInt(baseStyles.marginLeft || baseStyles.margin || '0', 10) || 0;

  const pTop = parseInt(baseStyles.paddingTop || baseStyles.padding || '0', 10) || 0;
  const pRight = parseInt(baseStyles.paddingRight || baseStyles.padding || '0', 10) || 0;
  const pBottom = parseInt(baseStyles.paddingBottom || baseStyles.padding || '0', 10) || 0;
  const pLeft = parseInt(baseStyles.paddingLeft || baseStyles.padding || '0', 10) || 0;

  const showMarginOverlay = showSpacingGuides || highlightedBoxPart === 'margin';
  const showPaddingOverlay = showSpacingGuides || highlightedBoxPart === 'padding';

  return (
    <div
      className="pointer-events-none absolute inset-0 z-40"
      style={{ overflow: 'visible' }}
    >
      {/* Box Model Overlays: Margin (Amber) */}
      {showMarginOverlay && (mTop > 0 || mRight > 0 || mBottom > 0 || mLeft > 0) && (
        <div
          className="absolute border border-dashed border-amber-500/80 bg-amber-500/20 pointer-events-none transition-all duration-75"
          style={{
            left: rect.left - mLeft,
            top: rect.top - mTop,
            width: rect.width + mLeft + mRight,
            height: rect.height + mTop + mBottom,
          }}
        >
          <span className="absolute top-1 left-1.5 text-[9px] font-mono font-bold text-amber-700 dark:text-amber-300">
            margin
          </span>
        </div>
      )}

      {/* Box Model Overlays: Padding (Emerald/Blue) */}
      {showPaddingOverlay && (pTop > 0 || pRight > 0 || pBottom > 0 || pLeft > 0) && (
        <div
          className="absolute border border-dashed border-sky-400/80 bg-sky-400/20 pointer-events-none transition-all duration-75"
          style={{
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
          }}
        >
          <span className="absolute top-1 left-1.5 text-[9px] font-mono font-bold text-sky-700 dark:text-sky-300">
            padding
          </span>
        </div>
      )}

      {/* Main Selection Highlight Frame (2px #3b97e3) */}
      <div
        className="absolute pointer-events-none border-2 border-[#3b97e3] transition-all duration-75 shadow-xs"
        style={{
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        }}
      >
        {/* Top-Left GrapesJS Pill Badge (.gjs-badge) */}
        <div
          className="pointer-events-auto absolute -top-6 left-0 flex items-center gap-1.5 bg-[#3b97e3] text-white text-[11px] font-medium px-2 py-0.5 rounded-t shadow-md select-none whitespace-nowrap z-50"
          title={`Type: ${selectedNode.type} (Click to inspect)`}
        >
          <Box className="h-3 w-3 opacity-90" />
          <span className="font-semibold">{label}</span>
          <span className="opacity-75 text-[10px] font-mono">{tagId}</span>
          {activeState !== 'default' && (
            <span className="ml-1 bg-amber-400 text-amber-950 text-[9px] font-bold px-1 rounded uppercase">
              :{activeState}
            </span>
          )}
        </div>

        {/* Top-Right GrapesJS Floating Toolbar (.gjs-toolbar) */}
        <div className="pointer-events-auto absolute -top-7 right-0 flex items-center gap-0.5 bg-[#3b97e3] text-white p-0.5 rounded-t shadow-md select-none z-50">
          {parentNode && onSelectParent && (
            <button
              type="button"
              onClick={onSelectParent}
              className="p-1 hover:bg-white/20 rounded cursor-pointer transition-colors"
              title="Select Parent Container"
            >
              <ArrowUp className="h-3 w-3" />
            </button>
          )}

          <div className="p-1 cursor-grab hover:bg-white/20 rounded transition-colors" title="Drag to Move">
            <GripVertical className="h-3 w-3" />
          </div>

          {onMoveNode && (
            <>
              <button
                type="button"
                onClick={() => onMoveNode('up')}
                className="p-1 hover:bg-white/20 rounded cursor-pointer transition-colors"
                title="Move Up"
              >
                <ChevronUp className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => onMoveNode('down')}
                className="p-1 hover:bg-white/20 rounded cursor-pointer transition-colors"
                title="Move Down"
              >
                <ChevronDown className="h-3 w-3" />
              </button>
            </>
          )}

          {onDuplicateNode && (
            <button
              type="button"
              onClick={onDuplicateNode}
              className="p-1 hover:bg-white/20 rounded cursor-pointer transition-colors"
              title="Duplicate (Ctrl+D)"
            >
              <Copy className="h-3 w-3" />
            </button>
          )}

          {onWrapInContainer && (
            <button
              type="button"
              onClick={onWrapInContainer}
              className="p-1 hover:bg-white/20 rounded cursor-pointer transition-colors"
              title="Wrap in Container"
            >
              <FolderPlus className="h-3 w-3" />
            </button>
          )}

          {onDeleteNode && (
            <button
              type="button"
              onClick={onDeleteNode}
              className="p-1 hover:bg-red-500 rounded cursor-pointer transition-colors"
              title="Delete Block (Del)"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* 8-Point Interactive Resizer Handles (.gjs-resizer-h) */}
        {!selectedNode.locked && (
          <>
            {/* Corners */}
            <div
              className="pointer-events-auto absolute -top-1 -left-1 w-2.5 h-2.5 bg-white border border-[#3b97e3] shadow-xs cursor-nwse-resize z-50"
              onPointerDown={(e) => handlePointerDown(e, 'tl')}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
            <div
              className="pointer-events-auto absolute -top-1 -right-1 w-2.5 h-2.5 bg-white border border-[#3b97e3] shadow-xs cursor-nesw-resize z-50"
              onPointerDown={(e) => handlePointerDown(e, 'tr')}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
            <div
              className="pointer-events-auto absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-white border border-[#3b97e3] shadow-xs cursor-nesw-resize z-50"
              onPointerDown={(e) => handlePointerDown(e, 'bl')}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
            <div
              className="pointer-events-auto absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-white border border-[#3b97e3] shadow-xs cursor-nwse-resize z-50"
              onPointerDown={(e) => handlePointerDown(e, 'br')}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />

            {/* Edges */}
            <div
              className="pointer-events-auto absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-2 bg-white border border-[#3b97e3] rounded-xs shadow-xs cursor-ns-resize z-50"
              onPointerDown={(e) => handlePointerDown(e, 'tc')}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
            <div
              className="pointer-events-auto absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-2 bg-white border border-[#3b97e3] rounded-xs shadow-xs cursor-ns-resize z-50"
              onPointerDown={(e) => handlePointerDown(e, 'bc')}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
            <div
              className="pointer-events-auto absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-3 bg-white border border-[#3b97e3] rounded-xs shadow-xs cursor-ew-resize z-50"
              onPointerDown={(e) => handlePointerDown(e, 'cl')}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
            <div
              className="pointer-events-auto absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-3 bg-white border border-[#3b97e3] rounded-xs shadow-xs cursor-ew-resize z-50"
              onPointerDown={(e) => handlePointerDown(e, 'cr')}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
          </>
        )}

        {/* Live HUD Dimension Tooltip during resize */}
        {resizingHandle && liveDimension && (
          <div className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 bg-slate-900/95 text-white text-[11px] font-mono px-2 py-0.5 rounded shadow-xl whitespace-nowrap z-50 border border-slate-700">
            {liveDimension.width} × {liveDimension.height} px
          </div>
        )}
      </div>
    </div>
  );
}
